import type { GetStaffMeResponse } from "@wallyo/contracts/console";
import { useCallback, useEffect, useRef, useState } from "react";
import { getStaffMe } from "../api/console-api";
import { useApi } from "../api/context";

export type StaffMeState = {
  me: GetStaffMeResponse | null;
  /** When `me` was last loaded, to say how old it is if a refresh fails. */
  loadedAt: Date | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
};

/**
 * `GET /console/me` when the console opens and again whenever the window
 * regains focus, so a role granted or removed shows without a reload. The
 * server still checks every request; this only keeps the screen honest.
 */
export function useStaffMe(): StaffMeState {
  const { client } = useApi();
  const [me, setMe] = useState<GetStaffMeResponse | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  const reload = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const next = await getStaffMe(client);
      if (!mounted.current) return;
      setMe(next);
      setLoadedAt(new Date());
      setError(null);
    } catch (failure) {
      // A session-ending error has already cleared the session: the app is
      // returning to sign-in. Anything else is shown with its request ID.
      if (mounted.current) setError(failure);
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    mounted.current = true;
    void reload();
    const onFocus = () => void reload();
    const onVisible = () => {
      if (document.visibilityState === "visible") void reload();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted.current = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);

  return { me, loadedAt, error, loading, reload: () => void reload() };
}
